import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useAuth } from '../auth/useAuth';
import { redemptionApi } from '../api/redemptionApi';
import { promotionApi } from '../api/promotionApi';
import { useProfile } from '../context/ProfileContext';
import {
  PlanLinkPreview,
  RedeemPlanLinkResult,
  PromotionPreview,
  RedeemPromotionResult,
} from '../types';

/**
 * Landing page for a redeem link (`/redeem/:token`). The token is either a ProShop plan link
 * (attaches the golfer to a plan and adds its credits) or a promo code (adds Caddie Credits).
 * The signed-in golfer previews what the link grants, then redeems it. Reached after sign-in
 * (RequireAuth).
 */
const RedeemPage: React.FC = () => {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile, refreshProfile } = useProfile();

  const [loading, setLoading] = useState(true);
  const [planPreview, setPlanPreview] = useState<PlanLinkPreview | null>(null);
  const [promoPreview, setPromoPreview] = useState<PromotionPreview | null>(null);
  const [redeeming, setRedeeming] = useState(false);
  const [planResult, setPlanResult] = useState<RedeemPlanLinkResult | null>(null);
  const [promoResult, setPromoResult] = useState<RedeemPromotionResult | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      // Try a plan link first; if the token isn't one, fall back to a promo code.
      const plan = await redemptionApi.preview(token);
      if (plan) {
        setPlanPreview(plan);
      } else {
        setPromoPreview(await promotionApi.preview(token));
      }
      setLoading(false);
    })();
  }, [token]);

  const redeemPlan = async () => {
    setRedeeming(true);
    try {
      const res = await redemptionApi.redeem(token, profile.userId, user?.email);
      setPlanResult(res);
      if (res.success) await refreshProfile();
    } finally {
      setRedeeming(false);
    }
  };

  const redeemPromo = async () => {
    setRedeeming(true);
    try {
      const res = await promotionApi.redeem(token, profile.userId, user?.email);
      setPromoResult(res);
      if (res.success) await refreshProfile();
    } finally {
      setRedeeming(false);
    }
  };

  const creditsLabel = (uploads?: number | null) =>
    uploads == null ? 'Unlimited Caddie Credits' : `${uploads.toLocaleString()} Caddie Credits`;

  const renderBody = () => {
    if (loading) {
      return (
        <div className="redeem-loading">
          <ProgressSpinner style={{ width: 48, height: 48 }} />
          <p>Checking your link…</p>
        </div>
      );
    }

    // --- Result screens ---
    if (planResult) {
      return planResult.success ? (
        <>
          <i className="pi pi-check-circle redeem-icon redeem-icon--ok" aria-hidden="true" />
          <h1>You're on {planResult.productName}!</h1>
          <p>
            {planResult.unlimited
              ? 'You now have unlimited Caddie Credits.'
              : `${planResult.creditsAdded.toLocaleString()} Caddie Credits added — ${planResult.creditsRemaining.toLocaleString()} available now.`}
          </p>
          <Button label="Start analyzing shots" icon="pi pi-camera" onClick={() => navigate('/upload')} />
        </>
      ) : (
        <>
          <i className="pi pi-times-circle redeem-icon redeem-icon--err" aria-hidden="true" />
          <h1>This link can't be used</h1>
          <p>{planResult.error ?? 'The plan link is no longer valid.'}</p>
          <Button label="Go to the app" outlined onClick={() => navigate('/play')} />
        </>
      );
    }

    if (promoResult) {
      return promoResult.success ? (
        <>
          <i className="pi pi-check-circle redeem-icon redeem-icon--ok" aria-hidden="true" />
          <h1>{promoResult.creditsAdded.toLocaleString()} Caddie Credits added!</h1>
          <p>
            {promoResult.unlimited
              ? 'You have unlimited Caddie Credits.'
              : `You now have ${promoResult.creditsRemaining.toLocaleString()} Caddie Credits available.`}
          </p>
          <Button label="Start analyzing shots" icon="pi pi-camera" onClick={() => navigate('/upload')} />
        </>
      ) : (
        <>
          <i className="pi pi-times-circle redeem-icon redeem-icon--err" aria-hidden="true" />
          <h1>This code can't be used</h1>
          <p>{promoResult.error ?? 'The promo code is no longer valid.'}</p>
          <Button label="Go to the app" outlined onClick={() => navigate('/play')} />
        </>
      );
    }

    // --- Plan-link preview ---
    if (planPreview) {
      if (planPreview.redeemed || planPreview.expired) {
        return (
          <>
            <i className="pi pi-exclamation-triangle redeem-icon redeem-icon--warn" aria-hidden="true" />
            <h1>{planPreview.redeemed ? 'Already redeemed' : 'Link expired'}</h1>
            <p>
              {planPreview.redeemed
                ? 'This plan link has already been used.'
                : 'This plan link has expired.'}{' '}
              Ask your CaddieSight contact for a new one.
            </p>
            <Button label="Go to the app" outlined onClick={() => navigate('/play')} />
          </>
        );
      }
      return (
        <>
          <h1>Unlock {planPreview.productName}</h1>
          <p className="redeem-plan-credits">{creditsLabel(planPreview.includedUploads)}</p>
          <p className="redeem-hint">
            Redeeming attaches your account to the {planPreview.productName} subscription and adds its Caddie
            Credits so your Digital Caddie can analyze your shots.
          </p>
          <Button
            label={`Redeem ${planPreview.productName}`}
            icon="pi pi-unlock"
            onClick={redeemPlan}
            loading={redeeming}
          />
        </>
      );
    }

    // --- Promo-code preview ---
    if (promoPreview) {
      if (promoPreview.redeemed || promoPreview.expired) {
        return (
          <>
            <i className="pi pi-exclamation-triangle redeem-icon redeem-icon--warn" aria-hidden="true" />
            <h1>{promoPreview.redeemed ? 'Already redeemed' : 'Code expired'}</h1>
            <p>
              {promoPreview.redeemed
                ? 'This promo code has already been used.'
                : 'This promo code has expired.'}{' '}
              Ask your CaddieSight contact for a new one.
            </p>
            <Button label="Go to the app" outlined onClick={() => navigate('/play')} />
          </>
        );
      }
      return (
        <>
          <h1>{promoPreview.name || 'Redeem your promo code'}</h1>
          <p className="redeem-plan-credits">
            {promoPreview.lieCount.toLocaleString()} Caddie Credits
          </p>
          {promoPreview.message && <p className="redeem-hint">{promoPreview.message}</p>}
          <Button label="Redeem code" icon="pi pi-unlock" onClick={redeemPromo} loading={redeeming} />
        </>
      );
    }

    // --- Not found ---
    return (
      <>
        <i className="pi pi-times-circle redeem-icon redeem-icon--err" aria-hidden="true" />
        <h1>Link not found</h1>
        <p>This link is not valid. Ask your CaddieSight contact for a new one.</p>
        <Button label="Go to the app" outlined onClick={() => navigate('/play')} />
      </>
    );
  };

  return (
    <div className="redeem-page">
      <div className="redeem-card">
        <img src={`${process.env.PUBLIC_URL}/caddiesight-mark.svg`} alt="" className="redeem-mark" />
        {renderBody()}
      </div>
    </div>
  );
};

export default RedeemPage;
